/**
 * HouseholdTab Component
 *
 * Content for the "Household" tab in SettingsDialog.
 * Lists all household members with avatars, roles, split dates,
 * and management controls for owners.
 */

import { useAuth } from '@/features/auth/hooks/use-auth';
import { ConfirmationDialog } from '@/shared/components/confirmation-dialog';
import { Icon, IconKey } from '@/shared/components/icon';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Switch } from '@/shared/components/ui/switch';
import { useCurrentHousehold } from '@/shared/hooks/use-current-household';
import { useHouseholdMembers } from '@/shared/hooks/use-household-members';
import {
  useDisableHouseholdSharing,
  useEnableHouseholdSharing,
  useRespondToShareRequest,
} from '@/shared/hooks/use-user';
import { useState } from 'react';
import { MemberRow } from './member-row';

export function HouseholdTab() {
  const { isOwner, userId } = useAuth();
  const { data: members, isLoading } = useHouseholdMembers();
  const { data: householdData } = useCurrentHousehold();
  const enableMutation = useEnableHouseholdSharing();
  const disableMutation = useDisableHouseholdSharing();
  const respondMutation = useRespondToShareRequest();
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const currentMember = members?.find((m) => m.id === userId);
  const isSharing = currentMember?.share_net_worth ?? false;

  if (isLoading) {
    return (
      <div className="space-y-3 py-2">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-36" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* Net Worth Sharing */}
      {isOwner && (
        <div className="mb-4 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Net Worth Sharing</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Allow members to include their accounts in a household-wide net worth view.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(enableMutation.isPending || disableMutation.isPending) && (
                <Icon icon={IconKey.Loading} size={16} className="animate-spin text-muted-foreground" />
              )}
              <Switch
                checked={householdData?.net_worth_sharing_enabled ?? false}
                disabled={enableMutation.isPending || disableMutation.isPending}
                onCheckedChange={(checked) => {
                  if (checked) {
                    enableMutation.mutate();
                  } else {
                    setShowDisableConfirm(true);
                  }
                }}
              />
            </div>
          </div>
          <ConfirmationDialog
            isOpen={showDisableConfirm}
            title="Disable net worth sharing?"
            description="This will remove all members' accounts from the household net worth view and reset their sharing preferences."
            confirmLabel="Disable"
            onConfirm={() => {
              disableMutation.mutate();
              setShowDisableConfirm(false);
            }}
            onCancel={() => setShowDisableConfirm(false)}
          />
        </div>
      )}

      {!isOwner && householdData?.net_worth_sharing_enabled && (
        <div className="mb-4 pb-4 border-b">
          <h3 className="text-sm font-medium">Net Worth Sharing</h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">
            The household owner has enabled net worth sharing. Choose whether to include your accounts.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={isSharing ? 'primary' : 'outline'}
              disabled={isSharing || respondMutation.isPending}
              onClick={() => respondMutation.mutate(true)}
            >
              Include my accounts
            </Button>
            <Button
              size="sm"
              variant={!isSharing ? 'primary' : 'outline'}
              disabled={!isSharing || respondMutation.isPending}
              onClick={() => respondMutation.mutate(false)}
            >
              Keep private
            </Button>
            <Badge variant={isSharing ? 'default' : 'secondary'}>
              {isSharing ? 'Sharing' : 'Private'}
            </Badge>
          </div>
        </div>
      )}

      {/* Section header */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-medium">Members</h3>
        {members && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {members.length}
          </Badge>
        )}
      </div>

      {/* Member list */}
      {members && members.length > 0 ? (
        <div className="divide-y">
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              isOwner={isOwner}
              isSelf={member.id === userId}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Icon icon={IconKey.Users} size={40} className="text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No members found</p>
        </div>
      )}
    </div>
  );
}
